/** Livrări (App-Livrari.dc.html) — clipuri acceptate, fără watermark; download MP4 + Share. */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClips } from '../api/hooks';
import { downloadClip } from '../api/endpoints';
import { resolveMediaUrl } from '../lib/media';
import { categoryLabel, formatDate, tierLabel } from '../lib/format';
import { EmptyState, PageHeader, Segmented, SkeletonCard } from '../components/ui';
import { ShareModal } from '../components/ShareModal';
import type { Clip } from '../api/types';

type Filter = 'toate' | 'organic' | 'reclame' | 'ultra';

export function LivrariPage() {
  const clips = useClips('livrat');
  const [filter, setFilter] = useState<Filter>('toate');
  const [shareClip, setShareClip] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Livrări — Reclame Video';
  }, []);

  const all = clips.data?.items ?? [];
  const items = all.filter((c) => {
    if (filter === 'toate') return true;
    if (filter === 'ultra') return c.tier === 'ultra';
    return c.category === filter;
  });

  return (
    <div>
      <PageHeader
        title="Livrări"
        subtitle="Toate clipurile acceptate, fără watermark — le poți re-descărca oricând sau trimite prin link public."
        actions={
          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'toate', label: 'Toate' },
              { value: 'organic', label: 'Organic' },
              { value: 'reclame', label: 'Reclame' },
              { value: 'ultra', label: 'Ultra' },
            ]}
          />
        }
      />

      {clips.isLoading ? (
        <div className="grid-auto">
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          illustration="🎞️"
          title="Aici apar clipurile tale gata de postat"
          body="Când accepți un clip din preview, îl găsești aici fără watermark, oricând."
          cta={<Link to="/creeaza" className="btn btn-primary">Creează primul clip</Link>}
        />
      ) : (
        <div className="grid-auto" data-testid="deliveries">
          {items.map((c) => (
            <DeliveryCard key={c.id} clip={c} onShare={() => setShareClip(c.id)} />
          ))}
        </div>
      )}

      {shareClip && <ShareModal clipId={shareClip} open onClose={() => setShareClip(null)} />}
    </div>
  );
}

function DeliveryCard({ clip, onShare }: { clip: Clip; onShare: () => void }) {
  async function download() {
    const res = await downloadClip(clip.id, 'final');
    const url = resolveMediaUrl(res.url);
    if (url) window.open(url, '_blank');
  }
  return (
    <div className="card" data-testid="delivery-card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Link to={`/clipuri/${clip.id}`} style={{ textDecoration: 'none', color: 'var(--ink)' }}>
        <div style={{ aspectRatio: clip.format.replace(':', '/'), maxHeight: 200, borderRadius: 12, border: '1px solid var(--line)', backgroundColor: 'var(--bg2)', backgroundImage: 'repeating-linear-gradient(135deg,rgba(120,105,90,.16) 0 8px,rgba(120,105,90,0) 8px 17px),linear-gradient(180deg,rgba(226,122,60,.28),rgba(0,0,0,.1))' }} />
        <p style={{ margin: '10px 0 0', fontWeight: 800, fontSize: 15 }}>{clip.title || 'Clip fără titlu'}</p>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--muted)' }}>
          {categoryLabel(clip.category)} · {tierLabel(clip.tier)} · {clip.format} · {formatDate(clip.deliveredAt)}
        </p>
      </Link>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-ghost btn-sm" data-testid="download-mp4" onClick={download} style={{ flex: 1 }}>
          ↓ MP4
        </button>
        <button className="btn btn-ghost btn-sm" data-testid="share-btn" onClick={onShare} style={{ flex: 1 }}>
          ↗ Share
        </button>
      </div>
    </div>
  );
}
